/**
 * Pure Routing-Logik der Intercom – BEWUSST ohne LiveKit-/Browser-Imports,
 * damit sie isoliert (Node/Vitest) testbar ist. `livekit.ts` nutzt exakt
 * diese Funktionen, sodass Test und Laufzeit dieselbe Entscheidung treffen.
 */

/** Routing-Ziele, die ein Panel über seine Participant-Metadata publisht. */
export interface PanelRoutes {
  /** Adressierte Ziel-Panels (gerichteter Intercom). */
  panels: string[];
  /** Partylines, in die aktuell gesprochen wird. */
  partylines: string[];
  /** Call-to-All aktiv. */
  all: boolean;
}

export interface HearInput {
  /** Eigene Panel-ID (Empfänger). */
  myPanelId: string | null;
  /** Panel-ID des Senders. */
  srcPanelId: string;
  /** Routing-Ziele des Senders. */
  routes: PanelRoutes;
  /** Panel-IDs, die wir aktiv mithören (Listen-Buttons). */
  listenPanels: string[];
  /** Partyline-IDs, die wir aktiv mithören (Partyline-/Listen-Buttons). */
  listenPartylines: string[];
}

/**
 * Zentrale Routing-Entscheidung: Soll der Empfänger den Sender hören?
 *
 * Regeln (in dieser Reihenfolge):
 *  1. Sich selbst hört man nie.
 *  2. Call-to-All erreicht alle.
 *  3. Gerichteter Intercom genau auf dieses Panel.
 *  4. Aktiver Listen-Button direkt auf das Sender-Panel.
 *  5. Sender spricht in eine Partyline, die wir aktiv mithören.
 */
export function decideHear(i: HearInput): boolean {
  if (i.srcPanelId === i.myPanelId) return false;
  if (i.routes.all) return true;
  if (i.myPanelId !== null && i.routes.panels.includes(i.myPanelId)) return true;
  if (i.listenPanels.includes(i.srcPanelId)) return true;
  if (
    i.routes.partylines.length > 0 &&
    i.listenPartylines.some((pl) => i.routes.partylines.includes(pl))
  ) {
    return true;
  }
  return false;
}

/** Baut die Routes-Metadaten aus dem lokalen Sende-Zustand (dedupliziert). */
export function buildRoutes(opts: {
  intercomTargets: Iterable<string>;
  partylineTargets: Iterable<string>;
  callToAll: boolean;
}): PanelRoutes {
  return {
    panels: Array.from(new Set(opts.intercomTargets)),
    partylines: Array.from(new Set(opts.partylineTargets)),
    all: opts.callToAll,
  };
}
